import { fabric } from "fabric";
import deleteIcon from '../../../assets/control/delete_icon.svg';
import scaleIcon from '../../../assets/control/scale_icon.svg';
import touchRotateIcon from '../../../assets/control/touch_rotate_icon.svg';
import rotateNinetyIcon from '../../../assets/control/rotate_ninety_icon.svg';

import DEFINE from "../define";

export default class CustomControls {
    /**
     *
     * @type {"ml" | "mr" | "mb" | "mt" | "tl" | "tr" | "bl" | "br"}
     * @memberof CustomControls
     */
    corner = "";
    /**
     *
     * @type {fabric.Group}
     * @memberof CustomControls
     */
    group = null;
    /**
     *
     * @type {fabric.Group | fabric.Object}
     * @memberof CustomControls
     */
    targetShape = null;

    scaleButton = null;
    closeButton = null;
    rotateButton = null;
    rotateNinetyButton = null;

    targetTag = "";

    eventMap = new Map();

    mousemovingEvent = null;

    prevScaleDistance = 0;
    defaultScaleDistance = 0;

    prevScale = 1;
    currentScale = 0;

    deltaAngle = 0;


    constructor() {
        this.eventMap.set(DEFINE.TAG.CONTROL_SCALE + "mousedown", this.beginScale.bind(this));
        this.eventMap.set(DEFINE.TAG.CONTROL_SCALE + "mousemove", this.scaling.bind(this));
        this.eventMap.set(DEFINE.TAG.CONTROL_SCALE + "mouseup", this.endScale.bind(this));

        this.eventMap.set(DEFINE.TAG.CONTROL_ROTATE + "mousedown", this.beginRotate.bind(this));
        this.eventMap.set(DEFINE.TAG.CONTROL_ROTATE + "mousemove", this.rotating.bind(this));
        this.eventMap.set(DEFINE.TAG.CONTROL_ROTATE + "mouseup", this.endRotate.bind(this));

        this.eventMap.set(DEFINE.TAG.CONTROL_ROTATE_NINETY + "mousedown", this.handleRotateNinetyDown.bind(this));

        this.eventMap.set(DEFINE.TAG.CONTROL_CLOSE + "mouseup", this.handleClose.bind(this));
    }

    /**
     * 
     * @param {"ml" | "mr" | "mb" | "mt" | "tl" | "tr" | "bl" | "br"} corner 
     * @param {fabric.Group} group 
     * @param {fabric.Group | fabric.Object} targetShape 
     */
    async init(corner, group, targetShape) {
        this.corner = corner;
        this.group = group;
        this.targetShape = targetShape || group;

        await this.createButtons();
        this.group.canvas.renderAll();
    }

    async createButtons() {
        const width = this.targetShape.getScaledWidth();
        const height = this.targetShape.getScaledHeight();
        
        const alignPoint = this.getAlignPoint(width, height);
        
        this.closeButton = await this.loadSvgToGroup(deleteIcon);

        this.closeButton.set({
            tag: DEFINE.TAG.CONTROL_CLOSE,
            scaleX: 0.1,
            scaleY: 0.1,
            left: alignPoint.x,
            top: alignPoint.y,
        });

        this.scaleButton = await this.loadSvgToGroup(scaleIcon);

        this.scaleButton.set({
            tag: DEFINE.TAG.CONTROL_SCALE,
            scaleX: 0.1,
            scaleY: 0.1,
            left: alignPoint.x + this.scaleButton.getScaledWidth() * 0.105,
            top: alignPoint.y,
        });

        this.rotateButton = await this.loadSvgToGroup(touchRotateIcon);
        this.rotateButton.set({
            tag: DEFINE.TAG.CONTROL_ROTATE,
            scaleX: 0.1,
            scaleY: 0.1,
            left: alignPoint.x,
            top: alignPoint.y + this.rotateButton.getScaledHeight() * 0.105,
        });

        this.rotateNinetyButton = await this.loadSvgToGroup(rotateNinetyIcon);
        this.rotateNinetyButton.set({
            tag: DEFINE.TAG.CONTROL_ROTATE_NINETY,
            scaleX: 0.1,
            scaleY: 0.1,
            left: alignPoint.x + this.rotateNinetyButton.getScaledWidth() * 0.105,
            top: alignPoint.y + this.rotateNinetyButton.getScaledHeight() * 0.105,
        });

        this.group.add(this.closeButton);
        this.group.add(this.scaleButton);
        this.group.add(this.rotateButton);
        this.group.add(this.rotateNinetyButton);

        this.group.setCoords();

        this.defaultScaleDistance = new fabric.Point(this.scaleButton.left, this.scaleButton.top).distanceFrom(new fabric.Point(0, 0));

        //計算斜率
        let slope = (this.rotateButton.top) / (this.rotateButton.left);

        this.deltaAngle = (Math.atan(slope) / (Math.PI / 180));

        this.closeButton.setCoords();
        this.scaleButton.setCoords();
        this.rotateButton.setCoords();
        this.rotateNinetyButton.setCoords();

        this.group.setCoords();

        this.controls = [
            this.closeButton,
            this.scaleButton,
            this.rotateButton,
            this.rotateNinetyButton
        ]

        this.switchControl(false);

    }

    loadSvgToGroup(src) {
        return new Promise((resolve, reject) => {
            fabric.loadSVGFromURL(src, (objects, options) => {
                const group = new fabric.util.groupSVGElements(objects, options);

                resolve(group);
            });
        })
    }

    getAlignPoint(width, height) {
        const alignPoint = new fabric.Point(0, 0);
        switch (this.corner) {
            case "tr":
                alignPoint.setXY(width * 0.5, -height * 0.5);
                break;
            case "br":
                alignPoint.setXY(width * 0.5, height * 0.5);
                break;
            case "tl":
                alignPoint.setXY(-width * 0.5, -height * 0.5);
                break;
            case "bl":
                alignPoint.setXY(-width * 0.5, height * 0.5);
                break;
            case "mt":
                alignPoint.setXY(0, -height * 0.5);
                break;
            case "mr":
                alignPoint.setXY(width * 0.5, 0);
                break;
            case "mb":
                alignPoint.setXY(0, height * 0.5);
                break;
            case "ml":
                alignPoint.setXY(-width * 0.5, 0);
                break;
        }
        return alignPoint;
    }

    //#region 註冊事件
    mousedown(o, tag) {
        console.log("custom control mouse down::", o);
        if (o.target) {
            if (o.target.tag) {
                if (o.target.tag.includes(tag)) {
                    o.target.set('lockMovementX', false);
                    o.target.set('lockMovementY', false);

                    if (o.subTargets[0] && o.subTargets[0].tag) {
                        this.targetTag = o.subTargets[0].tag;
                    } else {
                        this.targetTag = o.target.tag;
                    }

                    this.originPointer = o.pointer;

                    const mousedownEvent = this.eventMap.get(this.targetTag + "mousedown");

                    if(typeof mousedownEvent == "function") {
                        mousedownEvent(o);
                        this.mousemovingEvent = this.eventMap.get(this.targetTag + "mousemove");
                    }
                }
            }
        }
    }
    mousemove(o) {
        if(typeof this.mousemovingEvent == "function") {
            this.mousemovingEvent(o);
        }
    }
    mouseup(o) {
        const mouseupEvent = this.eventMap.get(this.targetTag + "mouseup");
        if(typeof mouseupEvent == "function") {
            mouseupEvent(o);
        }
        this.targetTag = "";
        this.mousemovingEvent = null;
    }
    //#endregion
    //#region 縮放功能
    beginScale(o) {
        this.group.set({
            "lockMovementX": true,
            "lockMovementY": true
        });

        const pointer = this.group.canvas.getPointer(o.e);

        const center = new fabric.Point(this.group.left, this.group.top);

        const newPoint = new fabric.Point(pointer.x - center.x, pointer.y - center.y);

        const newDistance = newPoint.distanceFrom(new fabric.Point(0, 0));

        this.prevScaleDistance = newDistance;
        this.currentScale = this.prevScaleDistance / this.defaultScaleDistance;
    }

    scaling(o) {
        const center = new fabric.Point(this.group.left, this.group.top);
        const newPoint = this.group.canvas.getPointer(o.e);
        const newDistance = newPoint.distanceFrom(center);

        const deltaScale = (newDistance - this.prevScaleDistance) / this.defaultScaleDistance;

        const scaleRate = this.prevScale / this.currentScale;

        const scale = Math.min(Math.max(this.prevScale + (deltaScale * scaleRate), 0.6 * scaleRate), 2 * scaleRate);

        const buttonScale = 0.1 / scale;
    
        this.group.set({
            scaleX: scale,
            scaleY: scale,
        });

        for(let i = 0; i < this.controls.length; i++) {
            this.controls[i].set({
                scaleX: buttonScale,
                scaleY: buttonScale,
            });
        }

        const width = this.targetShape.getScaledWidth();
        const height = this.targetShape.getScaledHeight();
        const alignPoint = this.getAlignPoint(width, height);
        this.controls[0].set({
            left: alignPoint.x,
            top: alignPoint.y,
        });
        this.controls[1].set({
            left: alignPoint.x + this.controls[1].getScaledWidth() * 1.05,
            top: alignPoint.y ,
        });
        this.controls[2].set({
            left: alignPoint.x,
            top: alignPoint.y + this.controls[2].getScaledHeight() * 1.05,
        });
        this.controls[3].set({
            left: alignPoint.x + this.controls[3].getScaledWidth() * 1.05,
            top: alignPoint.y + this.controls[3].getScaledHeight() * 1.05,
        });

        for(let i = 0; i < this.controls.length; i++) {
            this.controls[i].setCoords();
        }

        this.group.canvas.renderAll();
    }

    endScale(o) {
        this.group.set( {
            "lockMovementX": false,
            "lockMovementY": false
        });

        this.group.canvas.renderAll();

        this.prevScale = this.group.scaleX;
    }
    //#endregion
    //#region 旋轉功能
    beginRotate(o) {
        this.group.set({
            "lockMovementX": true,
            "lockMovementY": true
        });
    }
    rotating(o) {
        const center = new fabric.Point(this.group.left, this.group.top);
        const pointer = this.group.canvas.getPointer(o.e);
        //計算斜率
        let slope = (pointer.y - center.y) / (pointer.x - center.x);
        let angle = (Math.atan(slope) / (Math.PI / 180)) - this.deltaAngle;

        if (pointer.x < center.x)
            angle += 180;

        this.group.angle = angle;

        this.group.canvas.renderAll();
    }

    endRotate(o) {

    }
    //#endregion

    handleRotateNinetyDown(o) {
        if (this.group.angle % 90 == 0) {
            this.group.angle += 90;
        } else {
            this.group.angle = (Math.floor(this.group.angle / 90) + 1) * 90;
        }

        for(let i = 0; i < this.controls.length; i++) {
            this.controls[i].setCoords();
        }
        

        this.group.canvas.renderAll();
    }
    
    handleClose() {
        const canvas = this.group.canvas;

        canvas.remove(this.group);
        canvas.renderAll();
    }
    /**
     * 
     * @param {boolean} show 
     */
    switchControl(show) {
        for(let i = 0; i < this.controls.length; i++) {
            this.controls[i].set({
                visible: show
            });
        }

        console.log(this.controls);

        this.group.canvas.renderAll();
    }
}